import { IconLoader2, IconMail, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import {
  getNotificationPreferences,
  type NotificationPreferencesResponse,
  removeEmailPreferences,
  type UpdateEmailPreferencesRequest,
  updateEmailPreferences,
} from '@/api/notification-preferences';
import { getSupplierEmailToggle, updateSupplierEmailToggle } from '@/api/settings';
import { DetailSectionHeader } from '@/components/detail-page-primitives';
import { EmailPreferenceCard } from '@/components/EmailPreferenceCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';

export interface NotificationSettingsApi {
  getPreferences: () => Promise<NotificationPreferencesResponse>;
  updateEmail: (data: UpdateEmailPreferencesRequest) => Promise<unknown>;
  removeEmail: (emailAddress: string) => Promise<unknown>;
  getSupplierToggle: () => Promise<{ enabled: boolean }>;
  updateSupplierToggle: (enabled: boolean) => Promise<{ enabled: boolean }>;
}

export interface NotificationSettingsProps {
  api?: NotificationSettingsApi;
  description?: string;
  emptyEventTypesMessage?: string;
  supplierLabel?: string;
}

const DEFAULT_NOTIFICATION_SETTINGS_API: NotificationSettingsApi = {
  getPreferences: getNotificationPreferences,
  updateEmail: updateEmailPreferences,
  removeEmail: removeEmailPreferences,
  getSupplierToggle: getSupplierEmailToggle,
  updateSupplierToggle: updateSupplierEmailToggle,
};

export function NotificationSettings({
  api,
  description,
  emptyEventTypesMessage,
  supplierLabel,
}: NotificationSettingsProps = {}) {
  const { labels } = useSupplierDisplay();
  const instanceId = useId();

  const resolvedSupplierLabel = supplierLabel ?? labels.plural;
  const resolvedDescription =
    description ??
    'Manage which email addresses receive notifications and what types of notifications they receive.';
  const resolvedEmptyMessage =
    emptyEventTypesMessage ?? 'Notification preferences are not available for your account type.';

  const effectiveApi: NotificationSettingsApi = api ?? DEFAULT_NOTIFICATION_SETTINGS_API;

  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<NotificationPreferencesResponse | null>(null);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [recentlyAddedEmail, setRecentlyAddedEmail] = useState<string | null>(null);

  const [supplierEmailEnabled, setSupplierEmailEnabled] = useState<boolean>(true);
  const [isLoadingSupplierEmailToggle, setIsLoadingSupplierEmailToggle] = useState(false);
  const [isUpdatingSupplierEmailToggle, setIsUpdatingSupplierEmailToggle] = useState(false);

  const toggleId = `${instanceId}-supplier-emails-toggle`;
  const emailInputId = `${instanceId}-newEmail`;

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const data = await effectiveApi.getPreferences();
      setPreferences(data);
    } catch (_error) {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, [effectiveApi.getPreferences]);

  const loadSupplierEmailToggle = useCallback(async () => {
    try {
      setIsLoadingSupplierEmailToggle(true);
      const result = await effectiveApi.getSupplierToggle();
      setSupplierEmailEnabled(result.enabled);
    } catch (_error) {
      toast.error(`Failed to load ${resolvedSupplierLabel.toLowerCase()} email preferences`);
    } finally {
      setIsLoadingSupplierEmailToggle(false);
    }
  }, [effectiveApi.getSupplierToggle, resolvedSupplierLabel]);

  useEffect(() => {
    loadPreferences();
    loadSupplierEmailToggle();
  }, [loadPreferences, loadSupplierEmailToggle]);

  const handleToggleSupplierEmails = async (enabled: boolean) => {
    try {
      setIsUpdatingSupplierEmailToggle(true);
      const result = await effectiveApi.updateSupplierToggle(enabled);
      setSupplierEmailEnabled(result.enabled);
      toast.success(`${resolvedSupplierLabel} emails ${result.enabled ? 'enabled' : 'disabled'}`);
    } catch (_error) {
      toast.error(`Failed to update ${resolvedSupplierLabel.toLowerCase()} email preferences`);
      setSupplierEmailEnabled(!enabled);
    } finally {
      setIsUpdatingSupplierEmailToggle(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail || !preferences) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (preferences.emailPreferences.some((ep) => ep.emailAddress === newEmail)) {
      toast.error('This email address is already configured');
      return;
    }

    try {
      setIsUpdating(true);
      const umbrellaEvent = preferences.availableEventTypes.find((et) => et.isUmbrella);
      const defaultEventIds = umbrellaEvent ? [umbrellaEvent.id] : [];

      const emailToAdd = newEmail;
      await effectiveApi.updateEmail({
        emailAddress: emailToAdd,
        eventTypeIds: defaultEventIds,
        recipientRole: 'to',
      });

      setRecentlyAddedEmail(emailToAdd);
      await loadPreferences();
      setNewEmail('');
      setIsAddingEmail(false);
      toast.success('Email added successfully');
    } catch (_error) {
      toast.error('Failed to add email');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateEmail = async (
    emailAddress: string,
    eventTypeIds: number[],
    recipientRole: 'to' | 'cc'
  ) => {
    try {
      setIsUpdating(true);
      await effectiveApi.updateEmail({ emailAddress, eventTypeIds, recipientRole });
      await loadPreferences();
      toast.success('Notification preferences updated');
    } catch (_error) {
      toast.error('Failed to update notification preferences');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveEmail = async (emailAddress: string) => {
    try {
      setIsUpdating(true);
      await effectiveApi.removeEmail(emailAddress);
      await loadPreferences();
      toast.success('Email removed from notifications');
    } catch (_error) {
      toast.error('Failed to remove email');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card className="gap-0">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3">
            <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading notification preferences...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <Card className="gap-0">
        <CardContent className="py-12">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Unable to load notification preferences</p>
            <Button variant="outline" onClick={loadPreferences} className="mt-4">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (preferences.availableEventTypes.length === 0) {
    return (
      <Card className="py-0">
        <DetailSectionHeader icon={IconMail} title="Email Notifications" />
        <CardContent className="p-4 pt-3">
          <p className="text-sm text-muted-foreground">{resolvedEmptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="py-0">
      <DetailSectionHeader icon={IconMail} title="Email Notifications" />
      <CardContent className="space-y-6 p-4 pt-3">
        <p className="text-sm text-muted-foreground">{resolvedDescription}</p>
        <div className="flex items-center justify-between pb-4 border-b border-border">
          <div className="space-y-0.5 flex-1">
            <Label htmlFor={toggleId} className="text-sm font-medium">
              Send Emails to {resolvedSupplierLabel}
            </Label>
            <p className="text-sm text-muted-foreground">
              Enable or disable automatic email notifications to{' '}
              {resolvedSupplierLabel.toLowerCase()} when payments are processed
            </p>
          </div>
          <div className="flex items-center ml-4">
            {isLoadingSupplierEmailToggle || isUpdatingSupplierEmailToggle ? (
              <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <label
                htmlFor={toggleId}
                className="relative inline-flex items-center cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={supplierEmailEnabled}
                  onChange={(e) => handleToggleSupplierEmails(e.target.checked)}
                  disabled={isUpdatingSupplierEmailToggle}
                  className="sr-only peer"
                  id={toggleId}
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 relative" />
              </label>
            )}
          </div>
        </div>

        {preferences.emailPreferences.length > 0 ? (
          <div className="space-y-4">
            {preferences.emailPreferences.map((emailPref) => (
              <EmailPreferenceCard
                key={emailPref.emailAddress}
                email={emailPref.emailAddress}
                selectedEventIds={emailPref.eventTypeIds}
                recipientRole={emailPref.recipientRole ?? 'to'}
                availableEventTypes={preferences.availableEventTypes}
                onUpdate={(eventTypeIds, recipientRole) =>
                  handleUpdateEmail(emailPref.emailAddress, eventTypeIds, recipientRole)
                }
                onRemove={() => handleRemoveEmail(emailPref.emailAddress)}
                isUpdating={isUpdating}
                defaultExpanded={emailPref.emailAddress === recentlyAddedEmail}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-muted/50 rounded-lg border border-dashed border-border">
            <IconMail className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No email addresses configured</p>
            <p className="text-xs text-muted-foreground/70">
              Add an email address to start receiving notifications
            </p>
          </div>
        )}

        {isAddingEmail ? (
          <div className="border border-border rounded-lg p-4 bg-muted/50">
            <Label htmlFor={emailInputId} className="text-sm font-medium">
              Email Address
            </Label>
            <div className="flex gap-3 mt-2">
              <Input
                id={emailInputId}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1"
                disabled={isUpdating}
              />
              <Button onClick={handleAddEmail} disabled={!newEmail || isUpdating}>
                {isUpdating ? <IconLoader2 className="h-4 w-4 animate-spin" /> : 'Add'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingEmail(false);
                  setNewEmail('');
                }}
                disabled={isUpdating}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsAddingEmail(true)}
            className="w-full border-dashed"
          >
            <IconPlus className="h-4 w-4 mr-2" />
            Add Email Address
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationSettings;
